import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {Element | null} */
  #eventScope = null;
  #closing = false;

  connectedCallback() {
    super.connectedCallback();

    this.#eventScope = this.#focusScope;
    this.#eventScope.addEventListener('keyup', this.#onKeyUp);
    this.#eventScope.addEventListener('click', this.#onHeaderAction, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventScope?.removeEventListener('keyup', this.#onKeyUp);
    this.#eventScope?.removeEventListener('click', this.#onHeaderAction, true);
    this.#eventScope = null;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    const details = this.#getDetailsElement(event);
    if (details.classList.contains('menu-drawer__inline-accordion')) {
      reset(details);
      details.querySelector('summary')?.focus();
      event.stopPropagation();
      return;
    }
    this.#close(details);
  };

  /** Keep the original mobile header controls inside the menu focus area. */
  get #focusScope() {
    if (this.classList.contains('header__drawer--mobile')) {
      return this.closest('header-component') ?? this;
    }
    return this;
  }

  /** @param {MouseEvent} event */
  #onHeaderAction = (event) => {
    if (!this.isOpen || !(event.target instanceof Element)) return;
    if (event.target.closest('header-actions, .header-logo')) this.close();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen && !this.#closing ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.querySelectorAll('.menu-drawer__inline-accordion').forEach((group) => {
        reset(group);
      });
    }

    this.#closing = false;
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    // Commit the folded position before starting the reveal.
    details.querySelector('.menu-drawer')?.getBoundingClientRect();
    requestAnimationFrame(() => { if (!this.#closing && details.open) details.classList.add('menu-open'); });

    trapFocus(details === this.refs.details ? this.#focusScope : details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#closing = true;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    if (details === this.refs.details) removeTrapFocus();

    onAnimationEnd(details, () => {
      if (!this.#closing) return;
      this.#closing = false;
      reset(details);

      if (details === this.refs.details) {
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
        if (details.contains(document.activeElement)) summary.focus();
      } else {
        trapFocus(this.#focusScope);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  if (!element.classList.contains('menu-drawer__inline-accordion')) {
    element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  }
}
